import type { EntityState } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import type { LoginType } from '../../auth/models/auth.model';
import type { Page } from '../../common/page/page.model';
import type { ApiError } from '../../models/common.model';
import type { Loadable } from '../../models/loadable.model';
import { emptyLoadable } from '../../models/loadable.model';
import { toastService } from '../../toast/toast.service';
import type { Comment } from '../model/comment.interface';
import { CommentState } from '../model/comment.interface';
import type { CommentLoadRequest } from '../model/comment-load-request.interface';
import type { PerksResponse } from '../model/perks-response.interface';
import type { Author, Post, Tag } from '../model/post.interface';
import type { ContentTab } from '../model/tab.interface';
import { ContentType } from '../model/tab.interface';
import type { User } from '../model/user.interface';
import { PerksHelper } from '../perks.helper';
import {
  archiveComment,
  loadAuthors,
  loadContentTabs,
  loadCustomContent,
  loadPopularPosts,
  loadPost,
  loadPostComments,
  loadPostCounts,
  loadPosts,
  loadTabContent,
  loadTags,
  loadUser,
  postComment,
  postUserAction,
  updateUser,
  uploadAvatar,
} from './perks.thunk';

export const postAdapter = createEntityAdapter<Post>();
export const postAdapterSelectors = postAdapter.getSelectors();

export interface IPerksState {
  perks: Loadable<PerksResponse>;
  loginTypes?: LoginType[];
  contentTabs: Loadable<ContentTab[]>;
  customContentHtml: Record<string, string>;
  user: Loadable<User>;
  updatingUser: Loadable<any>;
  posts: Loadable<EntityState<Post, string>>;
  postsPage: Page<Post> | null;
  popularPosts: Loadable<EntityState<Post, string>>;
  addingToCart: Loadable<any>;
  buyNow: Loadable<any>;
  commentLoadRequest: Partial<CommentLoadRequest>;
  replyTo: string | null;
  tags: Loadable<Page<Tag>>;
  authors: Loadable<Page<Author>>;
  tabContentPages: Partial<Record<ContentType, { last: boolean; nextCursor: string | null } | null>>;
}

export const initialState: IPerksState = {
  perks: emptyLoadable(),
  loginTypes: [],
  contentTabs: emptyLoadable(),
  customContentHtml: {},
  user: emptyLoadable(),
  updatingUser: emptyLoadable(),
  posts: {
    value: postAdapter.getInitialState(),
    loaded: false,
    loading: false,
    error: undefined,
  },
  postsPage: null,
  popularPosts: {
    value: postAdapter.getInitialState(),
    loaded: false,
    loading: false,
  },
  addingToCart: emptyLoadable(),
  buyNow: emptyLoadable(),
  commentLoadRequest: {},
  replyTo: null,
  tags: emptyLoadable(),
  authors: emptyLoadable(),
  tabContentPages: {},
};

const perksSlice = createSlice({
  name: 'perks',
  initialState,
  reducers: {
    resetContent: () => initialState,
    replyTo: (state, action) => {
      state.replyTo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadContentTabs.pending, (state: IPerksState) => {
        state.contentTabs.loading = true;
        state.contentTabs.error = undefined;
      })
      .addCase(loadContentTabs.fulfilled, (state: IPerksState, action) => {
        state.contentTabs.loading = false;
        state.contentTabs.loaded = true;
        state.contentTabs.error = undefined;
        state.contentTabs.value = action.payload;
      })
      .addCase(loadContentTabs.rejected, (state: IPerksState, action) => {
        state.contentTabs.loading = false;
        state.contentTabs.loaded = true;
        state.contentTabs.error = action.payload;
      });
    builder
      .addCase(loadTabContent.pending, (state: IPerksState, action) => {
        state.perks.loading = true;
        state.perks.error = undefined;
        if (!action.meta.arg.loadMore) {
          state.tabContentPages[action.meta.arg.contentType] = null;
        }
      })
      .addCase(loadTabContent.fulfilled, (state: IPerksState, action) => {
        state.perks.loading = false;
        state.perks.loaded = true;
        state.tabContentPages[action.meta.arg.contentType] = { last: action.payload.last, nextCursor: action.payload.nextCursor ?? null };
        if (!state.perks.value) {
          state.perks.value = {} as PerksResponse;
        }
        const items = action.payload.content;
        const { contentType, loadMore } = action.meta.arg;
        switch (contentType) {
          case ContentType.VIDEO:
            state.perks.value.videos = loadMore
              ? [...(state.perks.value.videos ?? []), ...items]
              : items;
            break;
          case ContentType.ALBUM:
            state.perks.value.albums = loadMore
              ? [...(state.perks.value.albums ?? []), ...items]
              : items;
            break;
          case ContentType.PRODUCT:
            state.perks.value.products = loadMore
              ? [...(state.perks.value.products ?? []), ...items]
              : items;
            break;
          case ContentType.COLLECTIBLE:
            state.perks.value.collectibles = loadMore
              ? [...(state.perks.value.collectibles ?? []), ...items]
              : items;
            break;
          case ContentType.PROMOTION:
            state.perks.value.codes = loadMore
              ? [...(state.perks.value.codes ?? []), ...items]
              : items;
            break;
        }
      })
      .addCase(loadTabContent.rejected, (state: IPerksState, action) => {
        state.perks.loading = false;
        state.perks.error = action.payload;
      });
    builder
      .addCase(loadPosts.pending, (state: IPerksState) => {
        state.posts.loading = true;
        state.posts.error = undefined;
      })
      .addCase(loadPosts.fulfilled, (state: IPerksState, action) => {
        state.posts.loading = false;
        state.posts.loaded = true;
        state.posts.error = undefined;
        state.postsPage = action.payload;

        // Use adapter to update the entity state
        if (action.meta.arg.loadMore) {
          postAdapter.addMany(state.posts.value!, action.payload.content);
        } else {
          postAdapter.setAll(state.posts.value!, action.payload.content);
        }
      })
      .addCase(loadPosts.rejected, (state: IPerksState, action) => {
        state.posts.loading = false;
        state.posts.loaded = false;
        state.posts.error = action.payload;
      });
    builder
      .addCase(loadPost.pending, (state: IPerksState) => {
        state.posts.loading = true;
        state.posts.error = undefined;
      })
      .addCase(loadPost.fulfilled, (state: IPerksState, action) => {
        state.posts.loading = false;
        state.posts.loaded = true;
        state.posts.error = undefined;
        if (!state.posts.value) {
          state.posts.value = postAdapter.getInitialState();
        }
        // Initialize counts so loadPostCounts.fulfilled can find it
        if (action.payload && !action.payload.counts) {
          action.payload.counts = {
            loading: true,
            loaded: false,
            value: undefined,
          };
        }
        postAdapter.upsertOne(state.posts.value, action.payload);
      })
      .addCase(loadPost.rejected, (state: IPerksState, action) => {
        state.posts.loading = false;
        state.posts.loaded = false;
        state.posts.error = action.payload;
      });
    builder
      .addCase(loadPopularPosts.pending, (state: IPerksState) => {
        state.popularPosts.loading = true;
        state.popularPosts.error = undefined;
      })
      .addCase(loadPopularPosts.fulfilled, (state: IPerksState, action) => {
        state.popularPosts.loading = false;
        state.popularPosts.loaded = true;
        state.popularPosts.error = undefined;
        state.popularPosts.value = postAdapter.setAll(
          postAdapter.getInitialState(),
          action.payload.content
        );
      })
      .addCase(loadPopularPosts.rejected, (state: IPerksState, action) => {
        state.popularPosts.loading = false;
        state.popularPosts.loaded = false;
        state.popularPosts.error = action.payload;
      });
    builder
      .addCase(loadUser.pending, (state: IPerksState) => {
        state.user.loading = true;
        state.user.error = undefined;
      })
      .addCase(loadUser.fulfilled, (state: IPerksState, action) => {
        state.user.loading = false;
        state.user.loaded = true;
        state.user.error = undefined;
        state.user.value = action.payload;
      })
      .addCase(loadUser.rejected, (state: IPerksState, action) => {
        state.user.loading = false;
        state.user.loaded = false;
        state.user.error = action.payload;
      });
    builder
      .addCase(updateUser.pending, (state: IPerksState) => {
        state.updatingUser.loading = true;
        state.updatingUser.error = undefined;
      })
      .addCase(updateUser.fulfilled, (state: IPerksState, action) => {
        state.updatingUser.loading = false;
        state.updatingUser.loaded = true;
        state.updatingUser.error = undefined;
        state.user.value = action.payload;
      })
      .addCase(updateUser.rejected, (state: IPerksState, action) => {
        state.updatingUser.loading = false;
        state.updatingUser.loaded = false;
        state.updatingUser.error = action.payload;
      });
    builder
      .addCase(uploadAvatar.pending, (state: IPerksState) => {
        state.updatingUser.loading = true;
        state.updatingUser.loaded = false;
        state.updatingUser.error = undefined;
      })
      .addCase(uploadAvatar.fulfilled, (state: IPerksState, action) => {
        state.updatingUser.loading = false;
        state.updatingUser.loaded = true;
        state.updatingUser.error = undefined;
        state.user.value = action.payload;
      })
      .addCase(uploadAvatar.rejected, (state: IPerksState, action) => {
        state.updatingUser.loading = false;
        state.updatingUser.loaded = false;
        state.updatingUser.error = action.payload;
      });
    builder
      .addCase(loadPostCounts.pending, (state: IPerksState, action) => {
        const postId = action.meta.arg;
        if (!postId) {
          // Update all posts
          const allPosts = Object.values(state.posts.value?.entities ?? {});
          const updates = allPosts.map((post) => ({
            id: post.id,
            changes: {
              counts: {
                value: post.counts?.value,
                loading: true,
                loaded: false,
              },
            },
          }));
          postAdapter.updateMany(state.posts.value!, updates);
        } else {
          // Update specific post only
          const post = state.posts.value?.entities?.[postId];
          if (post) {
            postAdapter.updateOne(state.posts.value!, {
              id: postId,
              changes: {
                counts: {
                  value: post.counts?.value,
                  loading: true,
                  loaded: false,
                },
              },
            });
          }
        }
      })
      .addCase(loadPostCounts.fulfilled, (state: IPerksState, action) => {
        if (!state.posts.value) return;

        const updates = Object.keys(state.posts.value.entities)
          .filter((postId) => state.posts.value?.entities?.[postId]?.counts?.loading)
          .map((postId) => {
            const postCount = action.payload.find((c) => c.postId === postId);
            return {
              id: postId,
              changes: {
                counts: {
                  loading: false,
                  loaded: true,
                  value: postCount,
                  error: undefined,
                },
              },
            };
          });
        postAdapter.updateMany(state.posts.value, updates);
      });
    builder.addCase(loadCustomContent.fulfilled, (state: IPerksState, action) => {
      const { id, content } = action.payload;

      state.customContentHtml[id] = content;

      const customContent = state.perks.value?.customContents?.find((item) => item.id === id);
      if (customContent) {
        customContent.content = content;
      }
    });
    builder
      .addCase(loadTags.pending, (state: IPerksState) => {
        state.tags.loading = true;
        state.tags.error = undefined;
      })
      .addCase(loadTags.fulfilled, (state: IPerksState, action) => {
        state.tags.loading = false;
        state.tags.loaded = true;
        state.tags.error = undefined;
        state.tags.value = action.payload;
      })
      .addCase(loadTags.rejected, (state: IPerksState, action) => {
        state.tags.loading = false;
        state.tags.loaded = false;
        state.tags.error = action.payload;
      });
    builder
      .addCase(loadAuthors.pending, (state: IPerksState) => {
        state.authors.loading = true;
        state.authors.error = undefined;
      })
      .addCase(loadAuthors.fulfilled, (state: IPerksState, action) => {
        state.authors.loading = false;
        state.authors.loaded = true;
        state.authors.error = undefined;
        state.authors.value = action.payload;
      })
      .addCase(loadAuthors.rejected, (state: IPerksState, action) => {
        state.authors.loading = false;
        state.authors.loaded = false;
        state.authors.error = action.payload;
      });
    builder
      .addCase(loadPostComments.pending, (state: IPerksState, action) => {
        state.commentLoadRequest = {
          postId: action.meta.arg.postId,
          parentCommentId: action.meta.arg.parentCommentId,
        } as CommentLoadRequest;

        const existingPost = state.posts.value?.entities?.[action.meta.arg.postId];
        if (existingPost) {
          if (!existingPost.comments) {
            existingPost.comments = {
              value: undefined,
              loading: true,
              loaded: false,
              error: undefined,
            };
          } else {
            existingPost.comments.loading = true;
          }
        }
      })
      .addCase(loadPostComments.fulfilled, (state: IPerksState, action) => {
        const { postId, parentCommentId, comments } = action.payload;
        const existingPost = state.posts.value?.entities?.[postId];
        const isFirstPage = comments.number === 0 || comments.first;

        if (!existingPost) {
          state.commentLoadRequest = {};
          return;
        }

        let updatedComments;

        if (parentCommentId) {
          // Handle nested comment replies - use deep clone to ensure immutability
          updatedComments = JSON.parse(JSON.stringify(existingPost.comments));

          updatedComments.value?.content?.forEach((comment: Comment) => {
            PerksHelper.processCommentAction(parentCommentId, comment, (c) => {
              if (c.children) {
                // If first page, replace; otherwise append for pagination
                c.children = {
                  ...c.children,
                  content: isFirstPage
                    ? comments.content
                    : [...c.children.content, ...comments.content],
                  pageable: comments.pageable,
                  last: comments.last,
                  number: comments.number,
                  first: comments.first,
                  numberOfElements: comments.numberOfElements,
                  empty: comments.empty,
                };
              } else {
                c.children = comments;
              }
            });
          });

          updatedComments.loading = false;
          updatedComments.loaded = true;
          updatedComments.error = undefined;
        } else {
          // Handle top-level comments
          if (existingPost.comments?.value) {
            // If first page, replace comments; otherwise append for pagination
            updatedComments = {
              ...existingPost.comments,
              value: {
                ...existingPost.comments.value,
                content: isFirstPage
                  ? comments.content
                  : [...existingPost.comments.value.content, ...comments.content],
                pageable: comments.pageable,
                last: comments.last,
                number: comments.number,
                first: comments.first,
                numberOfElements: comments.numberOfElements,
                empty: comments.empty,
              },
              loading: false,
              loaded: true,
              error: undefined,
            };
          } else {
            // If no comments exist yet, create new structure
            updatedComments = {
              value: comments,
              loading: false,
              loaded: true,
              error: undefined,
            };
          }
        }

        state.commentLoadRequest = {};
        postAdapter.updateOne(state.posts.value!, {
          id: postId,
          changes: {
            comments: updatedComments,
          },
        });
      })
      .addCase(loadPostComments.rejected, (state: IPerksState, action) => {
        const postId = action.meta.arg.postId;
        const existingPost = state.posts.value?.entities?.[postId];

        state.commentLoadRequest = {};

        if (existingPost && existingPost.comments) {
          existingPost.comments.loading = false;
          existingPost.comments.loaded = false;
          existingPost.comments.error = action.payload as ApiError;
        }
      });
    builder.addCase(postUserAction.fulfilled, (state: IPerksState, action) => {
      const { postId, commentId, actionType } = action.meta.arg;

      if (!postId) return;

      const existingPost = state.posts.value?.entities?.[postId];
      if (!existingPost) return;

      // Handle post likes
      if (!commentId && (actionType === 'LIKE' || actionType === 'REMOVE_LIKE')) {
        const isLike = actionType === 'LIKE';
        const currentLikes = existingPost.counts?.value?.likes || 0;

        const updatedCounts = existingPost.counts?.value
          ? {
              ...existingPost.counts,
              value: {
                ...existingPost.counts.value,
                likes: isLike ? currentLikes + 1 : Math.max(0, currentLikes - 1),
              },
            }
          : existingPost.counts;

        postAdapter.updateOne(state.posts.value!, {
          id: postId,
          changes: {
            userLiked: isLike,
            counts: updatedCounts,
          },
        });
      }

      // Handle comment likes
      if (commentId && (actionType === 'LIKE' || actionType === 'REMOVE_LIKE')) {
        const isLike = actionType === 'LIKE';

        if (existingPost.counts?.value?.commentCounts) {
          const commentCounts = [...existingPost.counts.value.commentCounts];
          const commentCountIndex = commentCounts.findIndex((c) => c.commentId === commentId);

          if (commentCountIndex >= 0) {
            const currentLikes = commentCounts[commentCountIndex].likes || 0;
            commentCounts[commentCountIndex] = {
              ...commentCounts[commentCountIndex],
              likes: isLike ? currentLikes + 1 : Math.max(0, currentLikes - 1),
            };
          } else if (isLike) {
            commentCounts.push({ commentId, likes: 1 });
          }

          const updatedCounts = {
            ...existingPost.counts,
            value: {
              ...existingPost.counts.value!,
              commentCounts,
            },
          };

          // Update userLiked flag on the comment itself using deep clone
          const updatedComments = JSON.parse(JSON.stringify(existingPost.comments));

          const updateCommentLike = (comments: Comment[]): boolean => {
            for (const comment of comments) {
              if (comment.id === commentId) {
                comment.userLiked = isLike;
                return true;
              }
              if (comment.children?.content) {
                if (updateCommentLike(comment.children.content)) {
                  return true;
                }
              }
            }
            return false;
          };

          if (updatedComments?.value?.content) {
            updateCommentLike(updatedComments.value.content);
          }

          postAdapter.updateOne(state.posts.value!, {
            id: postId,
            changes: {
              comments: updatedComments,
              counts: updatedCounts,
            },
          });
        }
      }
    });
    builder
      .addCase(postComment.fulfilled, (state: IPerksState, action) => {
        const postId = action.meta.arg.postId;
        const parentCommentId = action.meta.arg.parentCommentId;
        const newComment = action.payload;
        const existingPost = state.posts.value?.entities?.[postId];

        if (!existingPost) {
          state.replyTo = null;
          return;
        }

        if (!parentCommentId) {
          // Add comment to top-level
          if (existingPost.comments?.value?.content) {
            const updatedComments = {
              ...existingPost.comments,
              value: {
                ...existingPost.comments.value,
                content: [newComment, ...existingPost.comments.value.content] as Comment[],
              },
            };

            // Increment comment count
            const updatedCounts = existingPost.counts?.value
              ? {
                  ...existingPost.counts,
                  value: {
                    ...existingPost.counts.value,
                    comments: existingPost.counts.value.comments + 1,
                  },
                }
              : existingPost.counts;

            postAdapter.updateOne(state.posts.value!, {
              id: postId,
              changes: {
                comments: updatedComments,
                counts: updatedCounts,
              },
            });
          }
        } else {
          // Add nested comment - use deep clone
          if (existingPost.comments?.value?.content) {
            const clonedComments = JSON.parse(JSON.stringify(existingPost.comments));

            clonedComments.value.content.forEach((comment: Comment) => {
              PerksHelper.processCommentAction(parentCommentId, comment, (c) => {
                if (c.children) {
                  c.children.content = [newComment, ...c.children.content] as Comment[];
                } else {
                  // Parent has no children loaded - create new children structure
                  const pageable = existingPost.comments?.value?.pageable || {
                    pageNumber: 0,
                    pageSize: PerksHelper.COMMENTS_CHILD_DEFAULT_PAGE_SIZE,
                    sort: PerksHelper.COMMENTS_DEFAULT_SORT,
                  };
                  c.hasChildren = true;
                  (c as any).children = {
                    first: true,
                    last: true,
                    empty: false,
                    number: 0,
                    numberOfElements: 1,
                    totalElements: 1,
                    totalPages: 1,
                    pageable,
                    sort: pageable.sort,
                    size: pageable.pageSize,
                    content: [newComment],
                  };
                }
              });
            });

            // Increment comment count
            const updatedCounts = existingPost.counts?.value
              ? {
                  ...existingPost.counts,
                  value: {
                    ...existingPost.counts.value,
                    comments: existingPost.counts.value.comments + 1,
                  },
                }
              : existingPost.counts;

            postAdapter.updateOne(state.posts.value!, {
              id: postId,
              changes: {
                comments: clonedComments,
                counts: updatedCounts,
              },
            });
          }
        }

        // Reset replyTo
        state.replyTo = null;
      })
      .addCase(postComment.rejected, () => toastService.error('Error', `Failed to post comment`));
    builder.addCase(archiveComment.fulfilled, (state: IPerksState, action) => {
      const { postId, commentId } = action.meta.arg;
      const existingPost = state.posts.value?.entities?.[postId];

      if (!existingPost || !existingPost.comments?.value?.content) {
        return;
      }

      // Deep clone comments to update the archived comment's state
      const updatedComments = JSON.parse(JSON.stringify(existingPost.comments));

      const updateCommentState = (comments: Comment[]): boolean => {
        for (const comment of comments) {
          if (comment.id === commentId) {
            comment.state = CommentState.ARCHIVED;
            return true;
          }
          if (comment.children?.content) {
            if (updateCommentState(comment.children.content)) {
              return true;
            }
          }
        }
        return false;
      };

      updateCommentState(updatedComments.value.content);

      // Decrement comment count
      const updatedCounts = existingPost.counts?.value
        ? {
            ...existingPost.counts,
            value: {
              ...existingPost.counts.value,
              comments: Math.max(0, existingPost.counts.value.comments - 1),
            },
          }
        : existingPost.counts;

      postAdapter.updateOne(state.posts.value!, {
        id: postId,
        changes: {
          comments: updatedComments,
          counts: updatedCounts,
        },
      });
    });
  },
});

export const { resetContent, replyTo } = perksSlice.actions;
export const perksReducer = perksSlice.reducer;
